import React from 'react';
import PropTypes from 'prop-types';
import { 
  getTableMappingsAsOptions, 
  getMappingsAsOptions 
} from '../../../utils/mappingUtils';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';

/**
 * Component for rendering a select input with options from a mapping
 * @param {Object} props - Component props
 * @param {string} props.name - The name attribute for the select input
 * @param {string|number} props.value - The current value
 * @param {Function} props.onChange - Function to call when value changes
 * @param {string} props.mappingString - Optional mapping string in format "id1=text1;id2=text2"
 * @param {string} props.tableName - Optional table name for table-based mapping
 * @param {string} props.fieldName - Optional field name for table-based mapping
 * @param {string} props.id - Optional id attribute for the select input
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.placeholder - Optional placeholder text for empty option
 * @param {boolean} props.includeEmpty - Whether to include an empty option
 * @param {string} props.emptyOptionText - Text for the empty option
 * @param {boolean} props.showIdInLabel - Whether to show the ID in the option label
 * @param {boolean} props.disabled - Whether the select is disabled
 * @param {boolean} props.required - Whether the select is required
 */
const MappedSelect = ({
  name,
  value,
  onChange,
  mappingString,
  tableName,
  fieldName,
  id,
  className,
  placeholder,
  includeEmpty = true,
  emptyOptionText = "All",
  showIdInLabel = true,
  disabled = false,
  required = false
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  
  // Get options from mapping
  let options = [];
  if (tableName && fieldName) {
    options = getTableMappingsAsOptions(tableName, fieldName);
  } else if (mappingString) {
    options = getMappingsAsOptions(mappingString);
  }

  const defaultClassName = `w-full rounded-md ${semantic.formBg} ${semantic.formBorder} ${semantic.formText} shadow-sm focus:border-link focus:ring-link`;
  const finalClassName = className || defaultClassName;

  return (
    <select
      id={id || name}
      name={name}
      value={value}
      onChange={onChange}
      className={finalClassName}
      disabled={disabled}
      required={required}
      aria-label={name}
    >
      {includeEmpty && (
        <option value="">{replace(placeholder || emptyOptionText)}</option>
      )}
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {showIdInLabel ? `${option.label} (${option.value})` : option.label}
        </option>
      ))}
    </select>
  );
};

/**
 * Component for rendering a select input with options from a table field mapping
 */
export const TableMappedSelect = ({
  name,
  value,
  onChange,
  tableName,
  fieldName,
  ...rest
}) => {
  return (
    <MappedSelect
      name={name}
      value={value}
      onChange={onChange}
      tableName={tableName}
      fieldName={fieldName}
      {...rest}
    />
  );
};

MappedSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  mappingString: PropTypes.string,
  tableName: PropTypes.string,
  fieldName: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  includeEmpty: PropTypes.bool,
  emptyOptionText: PropTypes.string,
  showIdInLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool
};

TableMappedSelect.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  tableName: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired
};

export default MappedSelect;

